/**
 * Lead Capture Form Types
 *
 * Types for the lead capture form system, enabling tour creators to embed
 * contact forms, email signups, and custom inquiry forms within hotspots.
 *
 * Supports both HTML (desktop/mobile) and 3D GUI (VR) rendering modes.
 */

import type { Timestamp } from 'firebase/firestore';

// ============================================================================
// FORM FIELD TYPES
// ============================================================================

/**
 * Available form field types
 * - text: Single-line text input
 * - email: Email input with validation
 * - tel: Phone number input
 * - textarea: Multi-line text input
 * - select: Dropdown selection
 * - checkbox: Boolean checkbox
 * - hidden: Hidden field (for tracking data)
 */
export type FormFieldType = 'text' | 'email' | 'tel' | 'textarea' | 'select' | 'checkbox' | 'hidden';

/**
 * Option for select fields
 */
export interface FormFieldOption {
  label: string;   // Display text
  value: string;   // Submitted value
}

/**
 * Configuration for a single form field
 */
export interface FormField {
  id: string;                      // Unique identifier
  name: string;                    // Field name (used in submission data)
  type: FormFieldType;
  label: string;                   // Display label
  placeholder?: string;            // Placeholder text
  required: boolean;
  options?: FormFieldOption[];     // For select fields
  defaultValue?: string;           // Pre-filled value
  maxLength?: number;              // Character limit for text fields
  pattern?: string;                // Regex pattern for validation
  errorMessage?: string;           // Custom validation error message
}

// ============================================================================
// FORM CONFIGURATION TYPES
// ============================================================================

/**
 * Predefined form types with default configurations
 * - email_capture: Simple email signup (1 field)
 * - contact: Standard contact form (name, email, message)
 * - inquiry: Detailed inquiry (name, email, phone, interest)
 * - custom: User-defined fields
 */
export type LeadFormType = 'email_capture' | 'contact' | 'inquiry' | 'custom';

/**
 * Complete lead capture form configuration
 * Stored within HotspotConfig.leadForm
 */
export interface LeadFormConfig {
  /** Whether the form is active */
  enabled: boolean;

  /** Form preset type - determines default fields if not customized */
  formType: LeadFormType;

  /** Form title displayed at top */
  title: string;

  /** Optional description below title */
  description?: string;

  /** Form fields to display */
  fields: FormField[];

  /** Submit button text */
  submitButtonText: string;

  /** Message shown after successful submission */
  successMessage: string;

  // -------------------------------------------------------------------------
  // Delivery Options
  // -------------------------------------------------------------------------

  /** Email address to receive submission notifications */
  notificationEmail?: string;

  /** External webhook URL (Zapier, HubSpot, Salesforce, etc.) */
  webhookUrl?: string;

  /** URL to redirect user after successful submission */
  redirectUrl?: string;

  /** Send confirmation email to submitter (requires email field) */
  sendConfirmation?: boolean;

  // -------------------------------------------------------------------------
  // Behavior Options
  // -------------------------------------------------------------------------

  /** Show form automatically when entering stage (gated content) */
  showOnEntry?: boolean;

  /** Allow user to dismiss form without submitting */
  allowDismiss?: boolean;

  /** Enable honeypot field for spam detection */
  honeypotEnabled?: boolean;

  /** Custom thank you page content (HTML allowed) */
  thankYouContent?: string;
}

// ============================================================================
// FORM SUBMISSION TYPES
// ============================================================================

/**
 * Device type for analytics tracking
 */
export type SubmissionDeviceType = 'desktop' | 'mobile' | 'tablet' | 'vr';

/**
 * Stored form submission record
 * Collection: sequences/{sequenceId}/formSubmissions
 */
export interface FormSubmission {
  /** Auto-generated document ID */
  id?: string;

  /** Parent sequence ID */
  sequenceId: string;

  /** Stage where form was submitted */
  stageId: string;

  /** Hotspot containing the form */
  hotspotId: string;

  /** User-submitted form data (field name -> value) */
  formData: Record<string, string>;

  /** Submission timestamp */
  submittedAt: Timestamp;

  /** Analytics session ID (hashed for privacy) */
  visitorSessionId: string;

  /** Device type used for submission */
  deviceType: SubmissionDeviceType;

  /** Referrer URL if available */
  referrer?: string;

  /** User agent string */
  userAgent?: string;

  /** Whether notification email was sent */
  emailSent?: boolean;

  /** Whether webhook was triggered */
  webhookSent?: boolean;

  /** Any delivery errors */
  deliveryErrors?: string[];
}

/**
 * Result returned from form submission cloud function
 */
export interface FormSubmissionResult {
  success: boolean;
  message: string;
  submissionId?: string;
  redirectUrl?: string;
  errors?: string[];
}

// ============================================================================
// FORM VALIDATION TYPES
// ============================================================================

/**
 * Validation error for a specific field
 */
export interface FieldValidationError {
  fieldId: string;
  fieldName: string;
  message: string;
}

/**
 * Form validation result
 */
export interface FormValidationResult {
  isValid: boolean;
  errors: FieldValidationError[];
}

// ============================================================================
// TIER-BASED LIMITS
// ============================================================================

/**
 * Form limits by subscription tier
 * -1 = unlimited
 */
export const FORM_LIMITS_BY_TIER = {
  free: 0,         // No forms allowed
  basic: 1,        // 1 form per sequence
  pro: -1,         // Unlimited
  enterprise: -1,  // Unlimited
  admin: -1        // Unlimited
} as const;

export type SubscriptionTier = keyof typeof FORM_LIMITS_BY_TIER;

/**
 * Check if a tier can create forms
 */
export function canCreateForms(tier: SubscriptionTier): boolean {
  return FORM_LIMITS_BY_TIER[tier] !== 0;
}

/**
 * Check if form limit is reached for a tier
 * @param tier User's subscription tier
 * @param currentFormCount Number of forms already in sequence
 */
export function isFormLimitReached(tier: SubscriptionTier, currentFormCount: number): boolean {
  const limit = FORM_LIMITS_BY_TIER[tier];
  if (limit === -1) return false; // Unlimited
  return currentFormCount >= limit;
}

// ============================================================================
// FORM PRESETS
// ============================================================================

/**
 * Predefined form configurations for quick setup
 */
export const FORM_PRESETS: Record<LeadFormType, Omit<LeadFormConfig, 'enabled'>> = {
  email_capture: {
    formType: 'email_capture',
    title: 'Stay Updated',
    description: 'Get notified about updates and exclusive content.',
    fields: [
      {
        id: 'email',
        name: 'email',
        type: 'email',
        label: 'Email Address',
        placeholder: 'Enter your email',
        required: true
      }
    ],
    submitButtonText: 'Subscribe',
    successMessage: 'Thanks for subscribing! We\'ll keep you updated.',
    allowDismiss: true,
    honeypotEnabled: true
  },

  contact: {
    formType: 'contact',
    title: 'Contact Us',
    description: 'Have a question? We\'d love to hear from you.',
    fields: [
      {
        id: 'name',
        name: 'name',
        type: 'text',
        label: 'Name',
        placeholder: 'Your name',
        required: true,
        maxLength: 100
      },
      {
        id: 'email',
        name: 'email',
        type: 'email',
        label: 'Email',
        placeholder: 'Your email',
        required: true
      },
      {
        id: 'message',
        name: 'message',
        type: 'textarea',
        label: 'Message',
        placeholder: 'How can we help you?',
        required: true,
        maxLength: 1000
      }
    ],
    submitButtonText: 'Send Message',
    successMessage: 'Message sent! We\'ll get back to you soon.',
    allowDismiss: true,
    honeypotEnabled: true
  },

  inquiry: {
    formType: 'inquiry',
    title: 'Request Information',
    description: 'Tell us what you\'re interested in.',
    fields: [
      {
        id: 'name',
        name: 'name',
        type: 'text',
        label: 'Name',
        placeholder: 'Your name',
        required: true,
        maxLength: 100
      },
      {
        id: 'email',
        name: 'email',
        type: 'email',
        label: 'Email',
        placeholder: 'Your email',
        required: true
      },
      {
        id: 'phone',
        name: 'phone',
        type: 'tel',
        label: 'Phone',
        placeholder: 'Your phone number',
        required: false
      },
      {
        id: 'interest',
        name: 'interest',
        type: 'select',
        label: 'I\'m interested in',
        required: true,
        options: [
          { label: 'General Information', value: 'general' },
          { label: 'Pricing & Plans', value: 'pricing' },
          { label: 'Schedule a Tour', value: 'tour' },
          { label: 'Partnership Opportunity', value: 'partnership' }
        ]
      }
    ],
    submitButtonText: 'Submit Request',
    successMessage: 'Thanks for your interest! We\'ll contact you shortly.',
    allowDismiss: true,
    honeypotEnabled: true
  },

  custom: {
    formType: 'custom',
    title: 'Custom Form',
    description: '',
    fields: [],
    submitButtonText: 'Submit',
    successMessage: 'Thank you for your submission!',
    allowDismiss: true,
    honeypotEnabled: true
  }
};

/**
 * Get a form preset by type
 */
export function getFormPreset(type: LeadFormType): Omit<LeadFormConfig, 'enabled'> {
  return { ...FORM_PRESETS[type] };
}

/**
 * Create a new form config from a preset
 */
export function createFormFromPreset(type: LeadFormType): LeadFormConfig {
  return {
    ...getFormPreset(type),
    enabled: true
  };
}
